import { useState, useEffect, useCallback } from "react";
import {
  CreditCard,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  MapPin,
  School,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
} from "lucide-react";
import AdminHeader from "@/components/common/AdminHeader";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import {
  getSubscriptionSummary,
  getSubscriptionDetail,
} from "@/services/subscriptionData";
import { getZoneSubscriptionData } from "@/services/zoneManager";

interface ChildSubscription {
  Child_Name: string;
  School: string;
  State: string;
  City: string;
  Child_Address: string;
  Lal_Lon_Km: string;
  Subscription_Start_Date: string;
  Subscription_End_Date: string;
  Status: string;
}

interface ParentSubscription {
  Username: string;
  User_Address: string;
  State: string;
  City: string;
  Children: ChildSubscription[];
}

interface SubscriptionSummary {
  Total_Subscriptions: number;
  Active_Subscriptions: number;
  Expiring_in_10_days: number;
  Expired_Subscriptions: number;
  Ready_For_Verification: number;
}

interface PaginatedSubscriptions {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: ParentSubscription[];
}

interface SubscriptionDetail {
  Active_Subscriptions: PaginatedSubscriptions;
  Expiring_in_10_days: PaginatedSubscriptions;
  Expired_Subscriptions: PaginatedSubscriptions;
  Ready_For_Verification: PaginatedSubscriptions;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_count: number;
  page_size: number;
}

interface TabPagination {
  active: PaginationData;
  expiring: PaginationData;
  ready: PaginationData;
}

type TabType = "active" | "inactive" | "expiring" | "ready"  | "expired";

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();

  if (s === "active") {
    return (
      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
        Active
      </span>
    );
  }

  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
      {status}
    </span>
  );
}

function ChildCard({ child }: { child: ChildSubscription }) {
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-bold text-gray-900 text-sm">
          {child.Child_Name}
        </p>

        <StatusBadge status={child.Status} />
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <School className="w-3.5 h-3.5 flex-shrink-0" />

        <span>{child.School}</span>

        <span className="text-gray-300 mx-1">·</span>

        <span>
          {child.City}, {child.State}
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />

        <span className="truncate">
          {child.Child_Address}
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <span className="font-medium text-gray-700">
          Distance:
        </span>

        <span>{child.Lal_Lon_Km} km</span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />

        <span>
          {child.Subscription_Start_Date} →{" "}
          {child.Subscription_End_Date}
        </span>
      </div>
    </div>
  );
}

function ParentCard({
  parent,
}: {
  parent: ParentSubscription;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div>
          <p className="font-bold text-gray-900">
            {parent.Username}
          </p>

          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" />

            {parent.City}, {parent.State}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-brand-700 bg-brand-50 border border-brand-100 px-2.5 py-1 rounded-full">
            {parent.Children.length}{" "}
            {parent.Children.length === 1
              ? "child"
              : "children"}
          </span>

          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-4 space-y-3 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500 font-medium flex items-center gap-1">
            <MapPin className="w-3 h-3" />

            {parent.User_Address}
          </p>

          {parent.Children.map((child, i) => (
            <ChildCard key={i} child={child} />
          ))}
        </div>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  count: number;
  parents: ParentSubscription[];
  emptyText: string;
  headerColor: string;
  countColor: string;
  pagination: PaginationData;
  onPageChange: (page: number) => void;
}

function SubscriptionSection({
  title,
  icon,
  count,
  parents,
  emptyText,
  headerColor,
  countColor,
  pagination,
  onPageChange,
}: SectionProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div
        className={`flex items-center justify-between px-5 py-3.5 border-b border-gray-100 ${headerColor}`}
      >
        <div className="flex items-center gap-2">
          {icon}

          <p className="font-bold text-gray-800 text-sm">
            {title}
          </p>
        </div>

        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-full ${countColor}`}
        >
          {count}
        </span>
      </div>

      <div className="p-4 space-y-3">
        {parents.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-6">
            {emptyText}
          </p>
        ) : (
          parents.map((parent, i) => (
            <ParentCard
              key={`${parent.Username}-${i}`}
              parent={parent}
            />
          ))
        )}

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() =>
                onPageChange(
                  Math.max(
                    1,
                    pagination.current_page - 1
                  )
                )
              }
              disabled={pagination.current_page <= 1}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />

              Previous
            </button>

            <span className="text-sm text-gray-500">
              Page{" "}
              <span className="font-semibold text-gray-800">
                {pagination.current_page}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {pagination.total_pages}
              </span>
            </span>

            <button
              type="button"
              onClick={() =>
                onPageChange(
                  Math.min(
                    pagination.total_pages,
                    pagination.current_page + 1
                  )
                )
              }
              disabled={
                pagination.current_page >=
                pagination.total_pages
              }
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next

              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SubscriptionDataPage() {
  const { canView, loading: permLoading } =
    useScreenPermission("subscription data");

  const {
    isBranchIncharge,
    isZoneManager,
    isTelecaller,
    branchCode,
    zoneCode,
  } = useRoleContext();

  const [summary, setSummary] =
    useState<SubscriptionSummary | null>(null);

  const [detail, setDetail] =
    useState<SubscriptionDetail | null>(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [activeTab, setActiveTab] =
    useState<TabType>("active");

  const [pages, setPages] = useState({
    active: 1,
    expiring: 1,
    expired: 1,
    ready: 1,
  });

  const bc =
    isBranchIncharge || isTelecaller
      ? branchCode
      : undefined;

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) {
        setLoading(true);
      }

      try {
        let summaryResponse;
        let detailResponse;

        if (isZoneManager && zoneCode) {
          [summaryResponse, detailResponse] =
            await Promise.all([
              getZoneSubscriptionData(
                zoneCode,
                true
              ),
              getZoneSubscriptionData(
                zoneCode,
                false,
                pages.active,
                pages.expiring,
                pages.expired,
                pages.ready
              ),
            ]);
        } else {
          [summaryResponse, detailResponse] =
            await Promise.all([
              getSubscriptionSummary(bc),
              getSubscriptionDetail(
                bc,
                pages.active,
                pages.expiring,
                pages.expired,
                pages.ready
              ),
            ]);
        }

        setSummary(
          (summaryResponse.data as any)?.Summary ??
            null
        );

        const detailData =
          (detailResponse.data as SubscriptionDetail) ??
          null;

        setDetail(detailData);
      } catch (error) {
        console.error(
          "Failed to load subscription data:",
          error
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      bc,
      isZoneManager,
      zoneCode,
      pages.active,
      pages.expiring,
      pages.ready,
      pages.expired,
    ]
  );

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load(true);
  };

  const handlePageChange = (
    tab: TabType,
    page: number
  ) => {
    setPages((prev) => ({
      ...prev,
      [tab]: page,
    }));
  };

  const SUMMARY_STATS = summary
    ? [
        {
          label: "Total",
          value: summary.Total_Subscriptions,
          color: "text-gray-800",
          bg: "bg-gray-50",
          border: "border-gray-200",
        },
        {
          label: "Active",
          value: summary.Active_Subscriptions,
          color: "text-green-700",
          bg: "bg-green-50",
          border: "border-green-100",
        },
        {
          label: "Expiring Soon",
          value: summary.Expiring_in_10_days,
          color: "text-amber-700",
          bg: "bg-amber-50",
          border: "border-amber-100",
        },
        {
          label: "Expired",
          value: summary?.Expired_Subscriptions,
          color: "text-red-700",
          bg: "bg-red-50",
          border: "border-red-100",
        },
        {
          label: "Ready to Verify",
          value: summary?.Ready_For_Verification,
          color: "text-brand-700",
          bg: "bg-brand-50",
          border: "border-brand-100",
        },
      ]
    : [];

  const TABS = [
    {
      key: "active" as TabType,
      title: "Active",
      count:
        detail?.Active_Subscriptions?.count ??
        summary?.Active_Subscriptions ??
        0,
      icon: (
        <CheckCircle className="w-4 h-4" />
      ),
      activeClass:
        "text-green-700 border-green-500 bg-green-50",
    },
    {
      key: "expiring" as TabType,
      title: "Expiring in 10 Days",
      count:
        detail?.Expiring_in_10_days?.count ??
        summary?.Expiring_in_10_days ??
        0,
      icon: <Clock className="w-4 h-4" />,
      activeClass:
        "text-amber-700 border-amber-500 bg-amber-50",
    },
     {
      key: "expired" as TabType,
      title: "Expired",
      count:
        detail?.Expired_Subscriptions?.count ??
        summary?.Expired_Subscriptions ??
        0,
      icon: <XCircle className="w-4 h-4" />,
      activeClass:
        "text-amber-700 border-amber-500 bg-amber-50",
    },
    {
      key: "ready" as TabType,
      title: "Ready for Verification",
      count:
        detail?.Ready_For_Verification?.count ??
        summary?.Ready_For_Verification ??
        0,
      icon: (
        <CheckCircle className="w-4 h-4" />
      ),
      activeClass:
        "text-green-700 border-green-500 bg-green-50",
    },
  ];

  const activeParents =
    detail?.Active_Subscriptions?.results ?? [];

  const expiringParents =
    detail?.Expiring_in_10_days?.results ?? [];

  const readyParents =
    detail?.Ready_For_Verification?.results ?? [];

  const expiredParents = detail?.Expired_Subscriptions?.results ?? [];

  const activePagination: PaginationData = {
    current_page: pages.active,
    total_pages:
      detail?.Active_Subscriptions?.total_pages ?? 1,
    total_count:
      detail?.Active_Subscriptions?.count ?? 0,
    page_size: 20,
  };

  const expiringPagination: PaginationData = {
    current_page: pages.expiring,
    total_pages:
      detail?.Expiring_in_10_days?.total_pages ?? 0,
    total_count:
      detail?.Expiring_in_10_days?.count ?? 0,
    page_size: 20,
  };

  const expiredPagination: PaginationData = {
    current_page: pages.expired,
    total_pages:
      detail?.Expired_Subscriptions?.total_pages ?? 0,
    total_count:
      detail?.Expired_Subscriptions?.count ?? 0,
    page_size: 20,
  };

  const readyPagination: PaginationData = {
    current_page: pages.ready,
    total_pages:
      detail?.Ready_For_Verification?.total_pages ?? 1,
    total_count:
      detail?.Ready_For_Verification?.count ?? 0,
    page_size: 20,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />

      <main className="max-w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-brand-500" />
            </div>

            <div>
              <h1 className="text-2xl font-extrabold text-gray-900">
                Subscription Data
              </h1>

              <p className="text-gray-500 text-sm">
                Overview of all active, inactive &
                expiring subscriptions
              </p>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${
                refreshing
                  ? "animate-spin"
                  : ""
              }`}
            />

            Refresh
          </button>
        </div>

        {permLoading || loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-brand-500 mr-2" />

            <p className="text-gray-500">
              Loading…
            </p>
          </div>
        ) : !canView ? (
          <div className="flex items-center justify-center py-32">
            <p className="text-gray-500">
              You don't have permission to view
              subscription data.
            </p>
          </div>
        ) : !summary ? (
          <div className="text-center py-24 text-gray-400">
            <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />

            <p className="font-medium">
              No subscription data available.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {SUMMARY_STATS.map((stat) => (
                <div
                  key={stat.label}
                  className={`${stat.bg} border ${stat.border} rounded-xl p-5 text-center`}
                >
                  <p
                    className={`text-3xl sm:text-4xl font-extrabold ${stat.color}`}
                  >
                    {stat.value}
                  </p>

                  <p className="text-gray-500 text-xs mt-1 font-medium">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            {detail && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="border-b border-gray-200">
                  <div className="flex overflow-x-auto">
                    {TABS.map((tab) => {
                      const isActive =
                        activeTab === tab.key;

                      return (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() =>
                            setActiveTab(tab.key)
                          }
                          className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                            isActive
                              ? tab.activeClass
                              : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {tab.icon}

                          {tab.title}

                          <span
                            className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              isActive
                                ? "bg-white/70"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {tab.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="p-4">
                  {activeTab === "active" && (
                    <SubscriptionSection
                      title="Active Subscriptions"
                      icon={
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      }
                      count={
                        detail.Active_Subscriptions
                          ?.count ?? 0
                      }
                      parents={activeParents}
                      emptyText="No active subscriptions."
                      headerColor="bg-green-50/60"
                      countColor="bg-green-100 text-green-700"
                      pagination={activePagination}
                      onPageChange={(page) =>
                        handlePageChange(
                          "active",
                          page
                        )
                      }
                    />
                  )}
                  {activeTab === "expiring" && (
                    <SubscriptionSection
                      title="Expiring in 10 Days"
                      icon={
                        <Clock className="w-4 h-4 text-amber-600" />
                      }
                      count={
                        detail.Expiring_in_10_days
                          ?.count ?? 0
                      }
                      parents={expiringParents}
                      emptyText="No subscriptions expiring soon."
                      headerColor="bg-amber-50/60"
                      countColor="bg-amber-100 text-amber-700"
                      pagination={expiringPagination}
                      onPageChange={(page) =>
                        handlePageChange(
                          "expiring",
                          page
                        )
                      }
                    />
                  )}

                   {activeTab === "expired" && (
                    <SubscriptionSection
                      title="Expired"
                      icon={
                        <XCircle className="w-4 h-4 text-red-500" />
                      }
                      count={
                        detail?.Expired_Subscriptions
                          ?.count ?? 0
                      }
                      parents={expiredParents}
                      emptyText="No expired subscriptions."
                      headerColor="bg-red-50/60"
                      countColor="bg-red-100 text-red-700"
                      pagination={expiredPagination}
                      onPageChange={(page) =>
                        handlePageChange(
                          "expired",
                          page
                        )
                      }
                    />
                  )}

                  {activeTab === "ready" && (
                    <SubscriptionSection
                      title="Ready for Verification"
                      icon={
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      }
                      count={
                        detail.Ready_For_Verification
                          ?.count ?? 0
                      }
                      parents={readyParents}
                      emptyText="No subscriptions ready for verification."
                      headerColor="bg-green-50/80"
                      countColor="bg-green-100 text-green-600"
                      pagination={readyPagination}
                      onPageChange={(page) =>
                        handlePageChange(
                          "ready",
                          page
                        )
                      }
                    />
                  )}
                </div>
              </div>
            )}

            {/* {summary.Expired_Subscriptions > 0 && (
              <div className="bg-white rounded-xl border border-red-100 shadow-sm p-5 flex items-center gap-4">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />

                <div>
                  <p className="font-bold text-gray-900">
                    Expired Subscriptions
                  </p>

                  <p className="text-sm text-gray-500">
                    Total expired:{" "}
                    <span className="font-bold text-red-600">
                      {
                        summary.Expired_Subscriptions
                      }
                    </span>
                  </p>
                </div>
              </div>
            )} */}
          </div>
        )}
      </main>
    </div>
  );
}